import { useEffect, useRef, useState } from 'react';

/** Cuánto tiempo se queda visible el aviso de "conexión restablecida". */
const RECOVERED_VISIBLE_MS = 4000;
/** Cada cuánto se refresca el contador de tiempo sin conexión. */
const TICK_MS = 20000;

export interface OnlineStatus {
  online: boolean;
  /** Momento (timestamp) en que se perdió la conexión, o null si hay conexión. */
  offlineSince: number | null;
  /** Milisegundos acumulados sin conexión. */
  offlineMs: number;
  /** true durante unos segundos justo después de recuperar la conexión. */
  justRecovered: boolean;
}

function readNavigatorOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

/**
 * Detecta si el equipo tiene conexión a internet.
 * Escucha los eventos nativos del navegador y mantiene el tiempo sin conexión.
 */
export default function useOnlineStatus(): OnlineStatus {
  const [online, setOnline] = useState<boolean>(readNavigatorOnline);
  const [offlineSince, setOfflineSince] = useState<number | null>(() =>
    readNavigatorOnline() ? null : Date.now()
  );
  const [justRecovered, setJustRecovered] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());

  const onlineRef = useRef(online);
  const recoveredTimer = useRef<number | null>(null);

  useEffect(() => {
    onlineRef.current = online;
  }, [online]);

  useEffect(() => {
    const clearRecoveredTimer = () => {
      if (recoveredTimer.current !== null) {
        window.clearTimeout(recoveredTimer.current);
        recoveredTimer.current = null;
      }
    };

    const handleOffline = () => {
      if (!onlineRef.current) return;
      onlineRef.current = false;
      clearRecoveredTimer();
      setOnline(false);
      setOfflineSince(Date.now());
      setNow(Date.now());
      setJustRecovered(false);
    };

    const handleOnline = () => {
      if (onlineRef.current) return;
      onlineRef.current = true;
      setOnline(true);
      setOfflineSince(null);
      setJustRecovered(true);
      clearRecoveredTimer();
      recoveredTimer.current = window.setTimeout(() => {
        setJustRecovered(false);
        recoveredTimer.current = null;
      }, RECOVERED_VISIBLE_MS);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Estado inicial real (por si la app carga ya sin conexión).
    if (!readNavigatorOnline()) {
      onlineRef.current = false;
      setOnline(false);
      setOfflineSince((prev) => prev ?? Date.now());
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearRecoveredTimer();
    };
  }, []);

  // Mientras está sin conexión, refresca el contador cada cierto tiempo.
  useEffect(() => {
    if (online) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [online]);

  const offlineMs = offlineSince ? Math.max(0, now - offlineSince) : 0;

  return { online, offlineSince, offlineMs, justRecovered };
}